import { StandardEvents } from '@shopify/events';

// Preview selection changes imagery only; never write purchase selections here.
function chooseApprovedPortraitPreview(approved, wanted) {
  const pool=approved.filter(x=>x.url && x.format===wanted.format && x.bw===wanted.bw && !x.secondary);
  const same=x=>['frame','background','composition','layout','pets'].every(key=>x[key]===wanted[key]);
  const sizeKey=value=>(String(value||'').match(/\d+(?:\.\d+)?/g)||[]).slice(0,2).map(Number).sort((a,b)=>a-b).join('x');
  const sameSize=x=>!wanted.nativeSize || !x.nativeSize || sizeKey(x.nativeSize)===sizeKey(wanted.nativeSize);
  const exact=pool.find(x=>x.orientation===wanted.orientation && same(x) && sameSize(x));
  // Horizontal mockups use multiple example pets across every physical format.
  // The number of
  // entered names must not disqualify an otherwise matching physical preview.
  // Keep all design choices strict and describe the example pet-count difference.
  const horizontalExample=wanted.orientation==='Horizontal' && pool
    .filter(x=>x.orientation==='Horizontal' && ['frame','background','composition','layout'].every(key=>x[key]===wanted[key]))
    .sort((a,b)=>Number(sameSize(b))-Number(sameSize(a)) || Math.abs(a.pets-wanted.pets)-Math.abs(b.pets-wanted.pets))[0];
  const vertical=wanted.orientation==='Horizontal' && pool.find(x=>x.orientation==='Vertical' && same(x));
  const score=x=>[['frame',32],['composition',16],['layout',8],['background',4],['orientation',2],['pets',1]].reduce((n,[key,weight])=>n+(x[key]===wanted[key]?weight:0),0);
  // The largest Color framed-poster size has an approved three-pet example.
  // This selects imagery only; wanted.pets remains the customer's name count.
  // Missing Full-body/background/layout matches use a labeled representative.
  const largestFramedPoster=!wanted.bw && wanted.format==='Framed Poster' && wanted.orientation==='Horizontal' && sizeKey(wanted.nativeSize)==='24x36';
  const sizeExample=largestFramedPoster && pool
    .filter(x=>x.orientation==='Horizontal' && x.pets===3 && sameSize(x))
    .sort((a,b)=>score(b)-score(a))[0];
  // A Vertical selection must never become a Horizontal representative merely
  // because that sample matches more design details. Preserve the physical
  // orientation first, then explicitly describe any representative differences.
  const representativePool=wanted.orientation==='Vertical' && pool.some(x=>x.orientation==='Vertical')
    ? pool.filter(x=>x.orientation==='Vertical') : pool;
  const entry=sizeExample||exact||horizontalExample||vertical||[...representativePool].sort((a,b)=>score(b)-score(a))[0];
  if(!entry)return null;
  const differences=[];
  for(const [key,label] of [['frame','frame'],['background','background'],['composition','composition'],['layout','name layout'],['pets','pet count']]){
    if(entry[key]!==wanted[key])differences.push(`${label}: ${key==='layout'?({none:'No name',top:'name at top',repeated:'repeated names'}[entry[key]]||entry[key]):entry[key]}`);
  }
  const verticalCaption=wanted.orientation==='Horizontal' && entry.orientation==='Vertical'?'Vertical example shown. Your portrait will be created in your selected horizontal size.':'';
  const orientationDifference=entry.orientation!==wanted.orientation && !verticalCaption?` Example orientation: ${entry.orientation}.`:'';
  const representative=differences.length?`Representative example shown (${differences.join('; ')}). Your selected options are saved.`:'';
  const depthRepresentative=entry.format==='Unframed Canvas';
  const depthCaption=depthRepresentative?'Canvas depth is representative; your selected canvas depth is retained.':'';
  const sizeCaption=entry.nativeSize && wanted.nativeSize && !sameSize(entry)?`Representative size example: ${entry.orientation==='Horizontal'?sizeKey(entry.nativeSize).split('x').reverse().join('×'):entry.nativeSize}. Your selected size is retained.`:'';
  return {entry,caption:[verticalCaption,representative,orientationDifference,depthCaption,sizeCaption].filter(Boolean).join(' '),exact:Boolean(exact&&entry===exact&&!depthRepresentative)};
}

class PersonalizedPortraitPreview extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.details = this.closest('.personalized-product-details--portrait');
    this.section = this.closest('.shopify-section');
    if (!this.details || !this.section) return;
    try { this.mapping = JSON.parse(this.dataset.previewMap || '{}') || {}; }
    catch { this.mapping = {}; }
    try { this.sizeOverrides = JSON.parse(this.dataset.sizeOverrides || '{}') || {}; }
    catch { this.sizeOverrides = {}; }
    try { this.variantMedia = JSON.parse(this.details.querySelector('[data-portrait-variant-media]')?.textContent || '{}'); }
    catch { this.variantMedia = {}; }
    if (this.mapping.version === 4 && !this.details.querySelector('portrait-format-selector')) {
      for (const sample of this.details.querySelectorAll('[data-name-example]')) {
        const entry = this.mapping.examples?.[sample.dataset.nameExample];
        if (!entry?.url) continue;
        const image = document.createElement('img');
        image.src = entry.url; image.alt = ''; image.width = entry.width; image.height = entry.height;
        sample.replaceChildren(image);
      }
      for (const button of this.details.querySelectorAll('[data-portrait-kind="frame"] [data-portrait-value]')) {
        const frame = ['Aspen Birch', 'Natural'].includes(button.dataset.portraitValue) ? 'Birch' : button.dataset.portraitValue;
        const entry = button.dataset.fixedFrameImage && frame === 'Birch' ? {url:button.dataset.fixedFrameImage} : this.mapping.previews?.White?.[frame];
        const image = button.querySelector('img');
        if (entry && image) { image.removeAttribute('srcset'); image.src = entry.url; }
      }
    }
    this.imageIds = new Set((this.dataset.imageIds || '').split(','));
    this.excludedIds = new Set((this.section.querySelector('[data-portrait-gallery]')?.dataset.portraitExcludedMediaIds || '').split(',').map(id => id.trim()));
    for (const id of this.excludedIds) this.imageIds.delete(id);
    this.variantId = this.dataset.variantId;
    this.variantMediaId = this.dataset.variantMediaId;
    this.nativeSize = this.dataset.nativeSize || '';
    this.nativeFrame = this.dataset.nativeFrame || '';
    try {this.approved = JSON.parse(this.dataset.approvedMap || '[]');} catch {this.approved=[];}
    this.hasFrame = this.dataset.hasFrame === 'true';
    this.pending = false;
    this.lastKey = null;
    this.generation = (this.generation || 0) + 1;
    this.details.addEventListener('change', (event) => {
      if (event.target.matches('[data-portrait-design-choice]')) {
        this.showDesignExample(event.target);
        return;
      }
      this.removeDesignExample();
      if (event.target.matches('[name="properties[Portrait Background Color]"], [name="properties[Portrait Style]"], [name="properties[Composition]"], [data-name-layout]')) { this.changedChoice = event.target; this.schedule(); }
    }, { signal });
    this.details.addEventListener('portrait:preview-change', () => this.schedule(), { signal });
    this.section.addEventListener(StandardEvents.productSelect, (event) => {
      if (!(event.target instanceof Element) || !this.details.contains(event.target)) return;
      const generation = ++this.generation;
      this.pending = true;
      event.promise.then(({ detail }) => {
        if (!this.isConnected || generation !== this.generation) return;
        this.pending = false;
        if (String(detail?.productId) !== this.dataset.productId || !detail?.html) return;
        const updatedPreview = Array.from(detail.html.querySelectorAll('la-portrait-preview'))
          .find(element => element.dataset.productId === this.dataset.productId);
        this.variantId = String(detail.resource?.id || '');
        this.variantMediaId = detail.resource ? String(updatedPreview?.dataset.variantMediaId || detail.resource.featured_media?.id || '') : '';
        this.nativeSize = updatedPreview?.dataset.nativeSize || '';
        this.nativeFrame = updatedPreview?.dataset.nativeFrame || '';
        this.hasFrame = updatedPreview?.dataset.hasFrame === 'true';
        this.lastKey = null;
        this.schedule();
      }).catch(() => {
        if (generation === this.generation) this.pending = false;
      });
    }, { signal });
    this.schedule();
  }

  removeDesignExample() {
    this.exampleRevision = (this.exampleRevision || 0) + 1;
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery?.querySelector('[data-design-example]')) return;
    const status = this.details.querySelector('[data-design-preview-status]');
    if (status) status.textContent = 'Product preview. Change a design option to view its Style example.';
    const replacement = gallery.cloneNode(true);
    replacement.querySelectorAll('[data-design-example]').forEach(node => node.remove());
    const slideshow = replacement.querySelector('slideshow-component');
    slideshow?.setAttribute('initial-slide', '0');
    if (slideshow) this.updateExampleControls(slideshow, 0);
    gallery.replaceWith(replacement);
    this.lastKey = null;
  }

  async showDesignExample(input) {
    this.removeDesignExample();
    const label = input.dataset.exampleLabel || input.value;
    const revision = this.exampleRevision;
    const status = this.details.querySelector('[data-design-preview-status]');
    const property = input.name.replace(/^properties\[|\]$/g, '');
    const entry = this.mapping.design_examples?.[property]?.[input.value];
    let url = input.dataset.exampleUrl || entry?.url;
    const unavailable = () => {
      if (status) status.textContent = `No approved example is mapped for ${input.value}. Your choice is saved; the gallery remains a representative product preview.`;
      this.lastKey = null;
      this.schedule();
    };
    if (!url) return unavailable();
    try {
      const parsed = new URL(url, location.href);
      if (parsed.protocol !== 'https:' || (parsed.hostname !== location.hostname && !/(^|\.)shopify\.com$/.test(parsed.hostname))) return unavailable();
      url = parsed.href;
    }
    catch { return unavailable(); }
    const ready = await new Promise(resolve => {
      const image = new Image();
      const timer = setTimeout(() => resolve(false), 8000);
      image.onload = () => { clearTimeout(timer); resolve(true); };
      image.onerror = () => { clearTimeout(timer); resolve(false); };
      image.src = url;
    });
    if (!this.isConnected || revision !== this.exampleRevision) return;
    if (!ready) return unavailable();
    const gallery = this.section.querySelector('media-gallery');
    const replacement = gallery?.cloneNode(true);
    const slideshow = replacement?.querySelector('slideshow-component');
    const slides = slideshow?.querySelector('slideshow-slides');
    const thumbnails = slideshow?.querySelector('[ref="thumbnails[]"]')?.parentElement;
    if (!slides || !thumbnails) return unavailable();
    const index = slides.querySelectorAll('slideshow-slide').length;
    const slide = document.createElement('slideshow-slide');
    slide.dataset.designExample = ''; slide.setAttribute('ref','slides[]'); slide.setAttribute('slide-id','portrait-design-example'); slide.className='portrait-preview-slide';
    const image = document.createElement('img'); image.src=url; image.alt=`Style example: ${label} — representative design example, not a combined live preview`;
    slide.append(image); slides.append(slide);
    const thumb = document.createElement('button'); thumb.type='button'; thumb.dataset.designExample=''; thumb.className='slideshow-controls__thumbnail button button-unstyled'; thumb.setAttribute('ref','thumbnails[]'); thumb.setAttribute('on:click',`/select/${index}`); thumb.setAttribute('aria-label',image.alt); thumb.append(image.cloneNode()); thumbnails.append(thumb);
    slideshow.setAttribute('initial-slide',String(index));
    this.updateExampleControls(slideshow, index);
    slideshow.removeAttribute('disabled');
    gallery.replaceWith(replacement);
    if (status) status.textContent=`Style example — ${label}: an approved representative example for this option, not a rendering of all selected choices.`;
  }

  updateExampleControls(slideshow, selected) {
    const count = slideshow.querySelectorAll('slideshow-slide').length;
    slideshow.style.setProperty('--slideshow-timeline', Array.from({ length: count }, (_, index) => `--slide-${index}`).join(','));
    slideshow.querySelectorAll('[ref="thumbnails[]"]').forEach((button, index) => button.setAttribute('aria-selected', String(index === selected)));
  }

  schedule() {
    const revision = this.revision = (this.revision || 0) + 1;
    if (this.pending) return;
    customElements.whenDefined('slideshow-component').then(() => {
      if (!this.isConnected || revision !== this.revision || this.pending) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = requestAnimationFrame(() => {
          if (this.isConnected && revision === this.revision && !this.pending) this.apply();
        });
      });
    });
  }

  async apply() {
    const revision = this.revision;
    // A default selection can finish before this module loads. Read the live picker,
    // not just the server's original selection or events emitted before connection.
    const options = this.details.querySelector('la-portrait-options');
    const picker = this.details.querySelector('variant-picker');
    if (options && picker && !this.formatOverride) {
      for (const kind of ['size', 'frame']) {
        const group = options.querySelector(`[data-portrait-kind="${kind}"]`);
        const selected = [...picker.querySelectorAll('input[data-option-name]:checked')]
          .find(input => input.dataset.optionName.trim().toLowerCase() === group?.dataset.portraitOption?.trim().toLowerCase());
        if (selected) {
          if (kind === 'size') this.nativeSize = selected.value;
          else { this.nativeFrame = selected.value; this.hasFrame = true; }
        }
      }
    }
    const background = this.details.querySelector('[name="properties[Portrait Background Color]"]:checked, input[type="hidden"][name="properties[Portrait Background Color]"]')?.value || '';
    if (this.approved?.length) {
      const formats = {'premium-framed-poster':'Framed Poster','fine-art-posters':'Unframed Poster','matte-canvas-framed-multi-color':'Framed Canvas','matte-canvas-stretched-1-25':'Unframed Canvas','matte-canvas-stretched-0-75-multi-size':'Unframed Canvas'};
      const format = formats[this.formatOverride?.product.handle || this.dataset.sourceHandle];
      const selected = [...(picker?.querySelectorAll('input[data-option-name]:checked') || [])];
      const orientation = this.formatOverride?.selection.orientation || selected.find(input=>input.dataset.optionName.toLowerCase()==='orientation')?.value || (this.orientation(this.nativeSize)==='horizontal'?'Horizontal':'Vertical');
      const frame = this.hasFrame ? (['Natural','Aspen Birch'].includes(this.nativeFrame)?'Birch':this.nativeFrame) : 'None';
      const composition = this.details.querySelector('[name="properties[Composition]"]:checked')?.value || 'Full body';
      const layout = this.details.querySelector('[data-name-layout]:checked')?.dataset.nameLayout || 'top';
      const bw = /black.*white/i.test(this.details.querySelector('[name="properties[Portrait Style]"]:checked, input[type="hidden"][name="properties[Portrait Style]"]')?.value || '');
      const dedicatedBW = bw && Boolean(this.details.querySelector('portrait-format-selector'));
      const names = layout === 'none' ? 1 : this.details.querySelector('[data-pet-name]')?.value.split(',').filter(name=>name.trim()).length || 1;
      const pendingFormat=dedicatedBW && this.formatOverride?.pending;
      const wanted={format,bw,frame,orientation,composition,layout,background,pets:names,nativeSize:this.nativeSize};
      const resolved=chooseApprovedPortraitPreview(this.approved,wanted);
      const entry=resolved?.entry;
      if (dedicatedBW) this.updateBlackWhiteNameCards(this.approved, wanted);
      const gallery=this.section.querySelector('media-gallery');
      if (gallery) {
        gallery.removeAttribute('data-portrait-preview-unavailable');
        gallery.querySelector('slideshow-component')?.removeAttribute('hidden');
      }
      if (!entry) {
        // Every supported format/design has approved media. Retain the current
        // image if an unsupported product has no approved map, never blank it.
        return;
      }
      const description=`Example ${format}: ${entry.orientation}, ${entry.frame==='None'?'unframed':entry.frame+' frame'}, ${entry.background} background, ${entry.composition}, ${entry.pets} pet${entry.pets===1?'':'s'}, ${entry.layout==='none'?'No name':entry.layout==='repeated'?'repeated names':'name at top'}.`;
      const label=[resolved.caption,pendingFormat?'Choose an available size.':'',description,'Your own pet and saved selections will feature in the finished artwork.'].filter(Boolean).join(' ');
      if(bw){
        this.renderBlackWhiteGallery(this.approved.filter(item=>item.bw && item.format===format && item.orientation===entry.orientation && item.frame===entry.frame && (!dedicatedBW || (format==='Framed Poster' && entry.orientation==='Vertical') || (item.composition===entry.composition && item.layout===entry.layout && item.pets===entry.pets && item.background===entry.background))).sort((a,b)=>Number(a.secondary)-Number(b.secondary)),entry,label);
        return;
      }
      this.addApprovedGallery(format);
      if(entry){this.renderPilot(entry,label);}else{this.renderPilot(null,label);}
      return;
    }
    if (this.mapping?.version === 4) {
      this.addApprovedGallery();
      const frame = this.hasFrame ? (['Aspen Birch', 'Natural'].includes(this.nativeFrame) ? 'Birch' : this.nativeFrame) : 'None';
      const composition = this.details.querySelector('[name="properties[Composition]"]:checked')?.value || 'Full body';
      const layout = this.details.querySelector('[data-name-layout]:checked')?.dataset.nameLayout || 'top';
      const orientationInput = this.formatOverride ? {value:this.formatOverride.selection.orientation} : [...(picker?.querySelectorAll('input[data-option-name]:checked') || [])].find(input => input.dataset.optionName.toLowerCase() === 'orientation');
      const horizontal = orientationInput ? /horizontal/i.test(orientationInput.value) : this.orientation(this.nativeSize) === 'horizontal';
      let entry = this.mapping.previews?.[background]?.[frame];
      let label = `${background}${this.hasFrame ? ' / ' + this.nativeFrame : ''} — sample portrait, not a live rendering of your pet.`;
      // Resolve the complete current state, independently of which control was
      // changed last. The supplied composition/name samples are White, vertical
      // and Black (or unframed); never pass them off as another combination.
      const half = /half/i.test(composition);
      const example = half ? 'half' : layout;
      const exactExample = !horizontal && background === 'White' &&
        (!this.hasFrame || frame === 'Black') && (!half || layout === 'top');
      const missing = [];
      if (exactExample && this.mapping.examples?.[example]) {
        entry = this.mapping.examples[example];
      } else {
        if (half) missing.push('Half body');
        if (layout !== 'top') missing.push(layout === 'none' ? 'No name' : 'Repeated name pattern');
      }
      if (horizontal) {
        missing.push(composition);
        const selected = [...picker.querySelectorAll('input[data-option-name]:checked')];
        const variant = this.variantMedia.variants?.find(variant => this.variantMedia.optionNames.every((name, index) => { const input = selected.find(input => input.dataset.optionName === name); return !input || input.value === variant.options[index]; }));
        entry = variant?.url ? { url: variant.url.replace('width=480', 'width=1600'), width: 1600, height: 1200 } : null;
        label = `${background}${this.hasFrame ? ' / ' + this.nativeFrame : ''} / Horizontal — representative product sample.`;
      }
      if (missing.length) label = 'Example portrait; your selected options are saved.';
      const key = JSON.stringify([entry?.url, label]);
      if (key === this.lastKey && this.section.querySelector('[data-pilot-preview]')) return;
      if (!entry?.url) { this.renderPilot(null, label); this.lastKey = null; return; }
      const ready = await new Promise(resolve => { const image = new Image(); image.onload = () => resolve(true); image.onerror = () => resolve(false); image.src = entry.url; });
      if (!this.isConnected || revision !== this.revision || this.pending) return;
      this.renderPilot(ready ? entry : null, ready ? label : 'This sample could not load.');
      this.lastKey = ready ? key : null;
      return;
    }
    if (this.mapping?.version === 3) {
      const frame = this.nativeFrame === 'Aspen Birch' ? 'Birch' : this.nativeFrame;
      const entry = this.mapping.previews?.[background]?.[frame];
      const key = JSON.stringify([background, frame]);
      if (key === this.lastKey && this.section.querySelector('[data-pilot-preview]')) return;
      if (!entry?.url) {
        this.renderPilot(null, `No approved sample is available for ${background} / ${frame}.`);
        return;
      }
      const ready = await new Promise(resolve => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => resolve(false);
        image.src = entry.url;
      });
      if (!this.isConnected || revision !== this.revision || this.pending) return;
      this.renderPilot(ready ? entry : null, ready ? `${background} / ${frame} — sample portrait, not a live rendering of your pet.` : `The approved sample for ${background} / ${frame} could not load.`);
      this.lastKey = ready ? key : null;
      return;
    }
    const layout = this.details.querySelector('[data-name-layout]:checked')?.value || 'Name(s) at top';
    const style = this.selectedStyle();
    const v2 = this.mapping?.version === 2;
    const key = JSON.stringify(v2 ? [this.variantId, background, this.nativeFrame, this.nativeSize, style] : [this.variantId, background, layout, style]);
    if (key === this.lastKey) return;
    if (v2) {
      const orientation = this.orientation(this.nativeSize);
      const group = this.resolveGroup(background, this.nativeSize);
      if (group) {
        const ready = await this.preloadGroup(group);
        if (!this.isConnected || revision !== this.revision || this.pending) return;
        if (ready && this.renderGroup(group, background, orientation)) { this.lastKey = key; return; }
      }
      const fallback = this.resolveGroup(background, this.nativeSize, this.nativeFrame, this.hasFrame, '');
      if (fallback && fallback !== group) {
        const ready = await this.preloadGroup(fallback);
        if (!this.isConnected || revision !== this.revision || this.pending) return;
        if (ready && this.renderGroup(fallback, background, orientation)) { this.lastKey = key; return; }
      }
      this.renderGroup(null);
    }
    const slideshow = this.section.querySelector('media-gallery slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;
    const slides = Array.from(slideshow.querySelectorAll('slideshow-slide[slide-id]'));
    const local = this.mapping.variants?.[this.variantId];
    const defaults = this.mapping.default;
    const combination = `${background}|${layout}`;
    const candidates = [
      local?.combinations?.[combination], defaults?.combinations?.[combination],
      local?.backgrounds?.[background], defaults?.backgrounds?.[background],
      local?.layouts?.[layout], defaults?.layouts?.[layout],
      this.variantMediaId, this.dataset.featuredMediaId,
    ];
    const id = candidates.map(value => String(value ?? '')).find(value =>
      /^\d+$/.test(value) && this.imageIds.has(value) && slides.some(slide => slide.getAttribute('slide-id') === value)
    );
    if (!id) return;
    this.lastKey = key;
    slideshow.select({ id }, undefined, { animate: false });
  }

  sizeKey(size) { return String(size).toLowerCase().replace(/["″\s]/g, '').replace(/×/g, 'x'); }

  updateBlackWhiteNameCards(approved, wanted) {
    for (const sample of this.details.querySelectorAll('[data-name-example]')) {
      const resolved=chooseApprovedPortraitPreview(approved,{...wanted,layout:sample.dataset.nameExample,pets:1});
      const entry=resolved?.entry;
      if (!entry) {
        sample.replaceChildren(document.createTextNode('Approved example unavailable for this selection'));
        continue;
      }
      let image = sample.querySelector('img');
      if (!image) { image = document.createElement('img'); sample.replaceChildren(image); }
      if (image.getAttribute('src') !== entry.url) image.src = entry.url;
      image.removeAttribute('srcset'); image.width = entry.width || 1600; image.height = entry.height || 1600;
      image.alt = `Black & White ${entry.format}, ${entry.orientation}, ${entry.composition}, ${entry.layout === 'none' ? 'No name' : entry.layout === 'repeated' ? 'Repeated name pattern' : 'One name'}. ${resolved.caption}`;
    }
  }

  renderBlackWhiteGallery(entries, selected, label) {
    const gallery=this.section.querySelector('media-gallery');
    if(!gallery || !entries.length || !selected)return;
    const signature=entries.map(entry=>entry.url).join('|');
    if(gallery.dataset.bwGallery===signature){
      const index=entries.indexOf(selected);
      gallery.querySelector('slideshow-component')?.select(Math.max(0,index),undefined,{animate:false});
      gallery.querySelector('[data-pilot-preview-status]').textContent=label;
      return;
    }
    const replacement=gallery.cloneNode(true);
    const slideshow=replacement.querySelector('slideshow-component');
    const slides=slideshow?.querySelector('slideshow-slides');
    if(!slides)return;
    replacement.dataset.bwGallery=signature;
    replacement.querySelectorAll('[data-pilot-preview-status],.media-gallery__grid,zoom-dialog,dialog').forEach(node=>node.remove());
    slideshow.classList.remove('slideshow--single-media');slideshow.removeAttribute('disabled');
    slides.replaceChildren();
    slideshow.querySelectorAll('slideshow-controls').forEach(node=>node.remove());
    const controls=document.createElement('slideshow-controls');
    controls.setAttribute('ref','slideshowControls');controls.setAttribute('pagination-position','bottom');controls.setAttribute('thumbnails','');
    const thumbnails=document.createElement('div');thumbnails.className='slideshow-controls__thumbnails portrait-preview-thumbnails';controls.append(thumbnails);slideshow.append(controls);
    entries.forEach((entry,index)=>{
      const slide=document.createElement('slideshow-slide');slide.className='portrait-preview-slide';slide.setAttribute('ref','slides[]');slide.setAttribute('slide-id',`bw-approved-${index}`);
      const image=document.createElement('img');image.src=entry.url;image.width=1600;image.height=1600;image.style.objectFit='contain';image.alt=`Black & White ${entry.format}, ${entry.orientation}, ${entry.composition}, ${entry.layout==='none'?'no name':entry.layout==='repeated'?'repeated name':'single name'}, ${entry.pets} pet sample, ${entry.view}`;image.loading=index===entries.indexOf(selected)?'eager':'lazy';slide.append(image);slides.append(slide);
      const thumb=document.createElement('button');thumb.type='button';thumb.className='slideshow-control slideshow-controls__thumbnail button button-unstyled';thumb.style.width='64px';thumb.style.height='64px';thumb.setAttribute('ref','thumbnails[]');thumb.setAttribute('on:click',`/select/${index}`);thumb.setAttribute('aria-label',image.alt);thumb.append(image.cloneNode());thumbnails.append(thumb);
    });
    const index=Math.max(0,entries.indexOf(selected));slideshow.setAttribute('initial-slide',String(index));this.updateExampleControls(slideshow,index);
    const status=document.createElement('p');status.dataset.pilotPreviewStatus='';status.setAttribute('role','status');status.textContent=label;replacement.append(status);
    gallery.replaceWith(replacement);
  }

  addApprovedGallery(format) {
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery || gallery.dataset.approvedSamplesAdded) return;
    const replacement = gallery.cloneNode(true);
    const slideshow = replacement.querySelector('slideshow-component');
    const slides = slideshow?.querySelector('slideshow-slides');
    const thumbs = slideshow?.querySelector('[ref="thumbnails[]"]')?.parentElement;
    if (!slides || !thumbs) return;
    const seen = new Set([...slides.querySelectorAll('img')].map(image => image.src.split('?')[0]));
    const additions = [...(this.mapping.gallery || []), ...this.approved.filter(entry => !entry.bw && entry.format===format && entry.orientation==='Horizontal')];
    for (const [i, entry] of additions.entries()) {
      if (seen.has(entry.url.split('?')[0])) continue;
      seen.add(entry.url.split('?')[0]);
      const index = slides.querySelectorAll('slideshow-slide').length;
      const slide = document.createElement('slideshow-slide');
      slide.className = 'portrait-preview-slide'; slide.setAttribute('ref', 'slides[]'); slide.setAttribute('slide-id', `approved-sample-${i}`);
      const image = document.createElement('img'); image.src = entry.url; image.alt = entry.format ? `${entry.format}, ${entry.frame}, ${entry.orientation}, ${entry.composition}, ${entry.pets} pet example, ${entry.view}` : 'Approved sample portrait'; image.width = entry.width; image.height = entry.height; image.loading = 'lazy'; image.style.objectFit = 'contain';
      slide.append(image); slides.append(slide);
      const thumb = document.createElement('button'); thumb.type = 'button'; thumb.className = 'slideshow-control slideshow-controls__thumbnail button button-unstyled'; thumb.style.width = '64px'; thumb.style.height = '64px'; thumb.setAttribute('ref', 'thumbnails[]'); thumb.setAttribute('on:click', `/select/${index}`); thumb.setAttribute('aria-label', `Approved sample ${i + 1}`); thumb.append(image.cloneNode()); thumbs.append(thumb);
    }
    replacement.dataset.approvedSamplesAdded = 'true';
    this.updateExampleControls(slideshow, 0);
    gallery.replaceWith(replacement);
  }

  renderPilot(entry, label) {
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery) return;
    let status = gallery.querySelector('[data-pilot-preview-status]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.pilotPreviewStatus = '';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      gallery.append(status);
    }
    status.textContent = label;
    // Keep original media in the DOM, but do not select a contradictory fallback.
    if (!entry?.url) {
      gallery.querySelectorAll('[data-pilot-preview], [data-pilot-preview-thumbnail]').forEach(node => node.remove());
      status.textContent = label;
      return;
    }
    // Shopify omits thumbnail controls for a product with one media item.
    // Update that slide directly so the landing product does not keep its card image.
    const single = gallery.querySelector('slideshow-component.slideshow--single-media');
    if (single) {
      const slide = single.querySelector('slideshow-slide');
      if (slide) {
        slide.dataset.pilotPreview = '';
        for (const image of gallery.querySelectorAll('img')) {
          image.removeAttribute('srcset');
          image.src = entry.url;
          image.alt = label;
          image.width = entry.width || 1600;
          image.height = entry.height || 1600;
          image.style.objectFit = 'contain';
        }
      }
      return;
    }
    const existing = gallery.querySelector('slideshow-slide[data-pilot-preview]');
    if (existing && entry) {
      const image = existing.querySelector('img');
      image.src = entry.url; image.alt = label;
      const thumbnail = gallery.querySelector('[data-pilot-preview-thumbnail] img');
      if (thumbnail) { thumbnail.src = entry.url; thumbnail.alt = label; }
      gallery.querySelector('slideshow-component')?.select({ id: 'portrait-pilot-sample' }, undefined, { animate: false });
      return;
    }
    if (!entry) {
      gallery.querySelectorAll('[data-pilot-preview], [data-pilot-preview-thumbnail]').forEach(node => node.remove());
      gallery.querySelector('slideshow-component')?.select({ index: 0 }, undefined, { animate: false });
      return;
    }
    const replacement = gallery.cloneNode(true);
    const slideshow = replacement.querySelector('slideshow-component');
    const slides = slideshow?.querySelector('slideshow-slides');
    const thumbnails = slideshow?.querySelector('[ref="thumbnails[]"]')?.parentElement;
    if (!slides || !thumbnails) return;
    const index = slides.querySelectorAll('slideshow-slide').length;
    const slide = document.createElement('slideshow-slide');
    slide.dataset.pilotPreview = ''; slide.className = 'portrait-preview-slide';
    slide.setAttribute('ref', 'slides[]'); slide.setAttribute('slide-id', 'portrait-pilot-sample');
    const image = document.createElement('img');
    image.src = entry.url; image.alt = label; image.width = entry.width || 1600; image.height = entry.height || 1600;
    image.style.objectFit = 'contain';
    slide.append(image); slides.append(slide);
    const thumb = document.createElement('button');
    thumb.type = 'button'; thumb.dataset.pilotPreviewThumbnail = '';
    thumb.className = 'slideshow-control slideshow-controls__thumbnail button button-unstyled';
    thumb.style.width = '64px'; thumb.style.height = '64px';
    thumb.setAttribute('ref', 'thumbnails[]'); thumb.setAttribute('on:click', `/select/${index}`);
    thumb.setAttribute('aria-label', 'Selected background and frame sample');
    thumb.append(image.cloneNode()); thumbnails.append(thumb);
    slideshow.setAttribute('initial-slide', String(index));
    this.updateExampleControls(slideshow, index);
    slideshow.removeAttribute('disabled');
    gallery.replaceWith(replacement);
  }

  validGroup(group, productMediaOnly = false) {
    const views = ['front', 'side', 'context'];
    return group && new Set(views.map(view => group[view]?.id)).size === 3 && views.every(view => {
      try {
        const entry = group[view], url = new URL(entry?.url);
        return /^gid:\/\/shopify\/MediaImage\/\d+$/.test(entry?.id) && !this.excludedIds?.has(entry.id.split('/').pop()) && url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' &&
          (!productMediaOnly || this.imageIds.has(entry.id.split('/').pop()));
      } catch { return false; }
    });
  }

  selectedStyle() {
    return this.details.querySelector('[name="properties[Portrait Style]"]:checked')?.value || 'Color';
  }

  resolveGroup(background, size, frame = this.nativeFrame, hasFrame = this.hasFrame, style = this.selectedStyle()) {
    const orientation = this.orientation(size);
    const styled = this.mapping.styles?.[style];
    if (styled && typeof styled === 'object') {
      const backgroundMap = styled.size_overrides?.[background];
      const sizes = (hasFrame ? backgroundMap?.[frame] : backgroundMap)?.[orientation];
      const sizeKey = Object.keys(sizes || {}).find(key => this.sizeKey(key) === this.sizeKey(size));
      if (sizeKey && this.validGroup(sizes[sizeKey])) return sizes[sizeKey];
      const previews = styled.previews?.[background];
      const group = (hasFrame ? previews?.[frame] : previews)?.[orientation];
      if (this.validGroup(group)) return group;
    }
    for (const [map, local] of [[this.mapping.size_overrides, false], [this.sizeOverrides, true]]) {
      const backgroundMap = map?.[background];
      const sizes = (hasFrame ? backgroundMap?.[frame] : backgroundMap)?.[orientation];
      const key = Object.keys(sizes || {}).find(key => this.sizeKey(key) === this.sizeKey(size));
      if (key && this.validGroup(sizes[key], local)) return sizes[key];
    }
    const backgroundMap = this.mapping.previews?.[background];
    const group = (hasFrame ? backgroundMap?.[frame] : backgroundMap)?.[orientation];
    return this.validGroup(group) ? group : null;
  }

  // Called by the native gallery only for a same-product V2 update with a complete next group.
  canPreserveGallery(detail) {
    if (this.mapping?.version !== 2 || String(detail?.productId) !== this.dataset.productId) return false;
    if (!this.section.querySelector('slideshow-slide[data-portrait-preview-view]')) return false;
    const updated = detail.html.querySelector('la-portrait-preview');
    if (!updated || updated.dataset.productId !== this.dataset.productId) return false;
    const background = this.details.querySelector('[name="properties[Portrait Background Color]"]:checked')?.value;
    return !!this.resolveGroup(background, updated.dataset.nativeSize || '', updated.dataset.nativeFrame, updated.dataset.hasFrame === 'true');
  }

  async preloadGroup(group) {
    // Only the requested three views are fetched. Failed requests can be retried on a later selection.
    this.loadedImages ??= new Map();
    return (await Promise.all(['front', 'side', 'context'].map(view => {
      const url = group[view].url;
      if (!this.loadedImages.has(url)) this.loadedImages.set(url, new Promise(resolve => {
        const image = new Image();
        image.onload = () => image.decode().then(() => resolve(true)).catch(() => { this.loadedImages.delete(url); resolve(false); });
        image.onerror = () => { this.loadedImages.delete(url); resolve(false); };
        image.src = url;
      }));
      return this.loadedImages.get(url);
    }))).every(Boolean);
  }

  orientation(size) {
    const vertical = /\bvertical\b/i.test(size), horizontal = /\bhorizontal\b/i.test(size);
    if (vertical !== horizontal) return vertical ? 'vertical' : 'horizontal';
    if (vertical || horizontal) return '';
    const dimensions = size.trim().match(/^(\d+(?:\.\d+)?)\s*["″]?\s*[x×]\s*(\d+(?:\.\d+)?)\s*["″]?\s*$/i);
    if (!dimensions) return '';
    const [, width, height] = dimensions.map(Number);
    return width > 0 && height > 0 && width !== height ? (width < height ? 'vertical' : 'horizontal') : '';
  }

  renderGroup(group, background = '', orientation = '') {
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery || (!group && !gallery.querySelector('[data-portrait-preview-view]'))) return false;
    if (group && gallery.querySelectorAll('slideshow-slide[data-portrait-preview-view]').length === 3) {
      // Keep the live carousel and its listeners, focus and original media intact.
      for (const view of ['front', 'side', 'context']) {
        gallery.querySelectorAll(`[data-portrait-preview-view="${view}"]`).forEach(node => {
          const label = `${background}, ${this.nativeFrame}, ${orientation}, ${view} representative view`;
          node.setAttribute('aria-label', label);
          const image = node.querySelector('img');
          image.src = group[view].url;
          image.alt = label;
        });
      }
      const slideshow = gallery.querySelector('slideshow-component');
      slideshow.select({ id: 'portrait-front' }, undefined, { animate: false });
      this.previewAnimation?.cancel();
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.previewAnimation = gallery.querySelector('[slide-id="portrait-front"] img')?.animate([{ opacity: 0.85 }, { opacity: 1 }], { duration: 140 });
      }
      return true;
    }
    const replacement = gallery.cloneNode(true);
    replacement.querySelectorAll('[data-portrait-preview-view]').forEach(node => node.remove());
    const slideshow = replacement.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (!scroller) return false;
    const originalCount = scroller.querySelectorAll('slideshow-slide').length;
    let thumbnails = slideshow.querySelector('[ref="thumbnails[]"]')?.parentElement;
    if (group && !thumbnails) {
      thumbnails = document.createElement('div');
      thumbnails.className = 'portrait-preview-thumbnails';
      slideshow.append(thumbnails);
    }
    if (group) ['front', 'side', 'context'].forEach((view, offset) => {
      const entry = group[view], index = originalCount + offset;
      const slide = document.createElement('slideshow-slide');
      slide.setAttribute('ref', 'slides[]');
      slide.setAttribute('slide-id', `portrait-${view}`);
      slide.dataset.portraitPreviewView = view;
      slide.className = 'portrait-preview-slide';
      slide.setAttribute('aria-label', `${background}, ${this.nativeFrame}, ${orientation}, ${view} representative view`);
      const img = document.createElement('img');
      img.src = entry.url;
      img.alt = slide.getAttribute('aria-label');
      img.loading = offset === 0 ? 'eager' : 'lazy';
      slide.append(img);
      scroller.append(slide);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'slideshow-control button button-unstyled slideshow-controls__thumbnail';
      button.dataset.portraitPreviewView = view;
      button.setAttribute('ref', 'thumbnails[]');
      button.setAttribute('on:click', `/select/${index}`);
      button.setAttribute('aria-label', img.alt);
      const thumb = img.cloneNode(); thumb.loading = 'lazy'; button.append(thumb);
      thumbnails.append(button);
    });
    slideshow.setAttribute('initial-slide', String(group ? originalCount : 0));
    const total = scroller.querySelectorAll('slideshow-slide').length;
    slideshow.style.setProperty('--slideshow-timeline', Array.from({ length: total }, (_, index) => `--slide-${index}`).join(','));
    slideshow.querySelectorAll('[ref="thumbnails[]"]').forEach((button, index) => button.setAttribute('aria-selected', String(index === (group ? originalCount : 0))));
    slideshow.removeAttribute('disabled');
    gallery.replaceWith(replacement);
    return true;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.previewAnimation?.cancel();
    this.generation++;
    this.revision++;
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('la-portrait-preview')) customElements.define('la-portrait-preview', PersonalizedPortraitPreview);
